import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import FishBoneDiagram from '../components/FishBoneDiagram';
import FishBoneCauseSection from '../components/FishBoneCauseSection';
import ProjectDateField from '../components/ProjectDateField';
import ActionPlan from '../components/ActionPlan';
import RankingComponent from '../components/RankingComponent';
import DomainSubdomainSelector from '../components/DomainSubdomainSelector';
import ProjectTitleInput from '../components/ProjectTitleInput';
import { RankingItem } from '../types/project-form';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  Stack,
  SelectChangeEvent,
  ListSubheader,
  InputAdornment,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  ProjectFormData,
  ProblemStatementParts,
  CHS_DOMAINS,
  CHS_SUBDOMAINS,
  CHS_ROLES,
  Participant,
  ProjectStatus
} from '../types/project-form';
import { Project } from '../types/project';
import { getCurrentUser } from '../services/userService';
import { UserRole } from '../types/user';
import { projectService } from '../services/api';
import { validateFormData, validateTeamMembers, validateParticipants, MIN_TEAM_MEMBERS, MAX_TEAM_MEMBERS, PHONE_LENGTH_MESSAGE, PHONE_DUPLICATE_MESSAGE } from '../utils/formValidation';
import { scrollToFirstError } from '../utils/formHelpers';
import { checkRequiredFields } from '../utils/projectHelpers';
import TopBar from '../components/TopBar';
import FormActions from '../components/FormActions';
import { initialCHSTeamMember, initialParticipant, initialFormData, initialProblemStatementParts } from '../constants/projectConstants';

// Constants for CQI Project Forms

const PROBLEM_STATEMENT_BLANK = '____________';

// Compose the guided problem statement parts into a single sentence.
// Returns '' when all parts are empty so required-field validation still works.
const composeProblemStatement = (parts: ProblemStatementParts): string => {
  const allEmpty = Object.values(parts).every(v => !v || !v.trim());
  if (allEmpty) return '';
  const blank = (v?: string) => (v && v.trim() ? v.trim() : PROBLEM_STATEMENT_BLANK);
  return `In ${blank(parts.context)}, the issue is ${blank(parts.issue)}. ` +
    `Out of ${blank(parts.denominator)}, only ${blank(parts.numerator)} was reached, ` +
    `resulting in ${blank(parts.gap)}. This is mainly due to ${blank(parts.rootCause)}, ` +
    `which if not addressed, may lead to ${blank(parts.implication)}.`;
};

// Parse a previously composed problem statement back into its template parts.
// Falls back to empty parts for legacy free-text statements.
const parseProblemStatement = (statement?: string): ProblemStatementParts => {
  const match = statement?.match(
    /^In ([\s\S]*?), the issue is ([\s\S]*?)\. Out of ([\s\S]*?), only ([\s\S]*?) was reached, resulting in ([\s\S]*?)\. This is mainly due to ([\s\S]*?), which if not addressed, may lead to ([\s\S]*?)\.?$/
  );
  if (!match) return { ...initialProblemStatementParts };
  const unblank = (v: string) => (v === PROBLEM_STATEMENT_BLANK ? '' : v);
  return {
    context: unblank(match[1]),
    issue: unblank(match[2]),
    denominator: unblank(match[3]),
    numerator: unblank(match[4]),
    gap: unblank(match[5]),
    rootCause: unblank(match[6]),
    implication: unblank(match[7])
  };
};

// Inline fill-in-the-blank input used inside the problem statement template sentence.
const ProblemBlank: React.FC<{
  placeholder: string;
  width: number;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ placeholder, width, value, onChange }) => (
  <TextField
    variant="standard"
    size="small"
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    sx={{
      width,
      mx: 0.5,
      verticalAlign: 'baseline',
      '& .MuiInput-input': { textAlign: 'center' }
    }}
  />
);




// CHS constants (CHS_DOMAINS, CHS_SUBDOMAINS, CHS_ROLES) are now imported from project-form.ts


interface AddProjectProps {
  onLogout: () => void;
}

const AddProject = ({ onLogout }: AddProjectProps): React.JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check if we're editing an existing project
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);

  // Check user role - only facility users can add projects
  useEffect(() => {
    const user = getCurrentUser();
    if (!user || user.role !== UserRole.FACILITY_USER) {
      navigate('/projects', {
        state: {
          message: 'Access denied: Only facility users can add projects',
          error: true
        }
      });
      return;
    }

    // No longer auto-filling location information
  }, [navigate]);

  // State for form data with proper type from project-form.ts
  // Use initialFormData with status forcibly set to 'ongoing' for submission
  const [formData, setFormData] = useState<ProjectFormData>({
    ...initialFormData,
    status: 'ongoing' as ProjectStatus,
    identifiedProblems: [''], // Initialize with one empty problem field
    problemRankings: [] // Initialize empty rankings
  });

  // State for form validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof ProjectFormData, string>>>({});

  // State for team member validation errors
  const [teamMemberErrors, setTeamMemberErrors] = useState<Record<number, Record<string, string>>>({});
  
  // State for participant validation errors
  const [participantErrors, setParticipantErrors] = useState<Record<number, Record<string, string>>>({});

  // State for submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [draftSuccessMessage, setDraftSuccessMessage] = useState('');
  const [showBackDialog, setShowBackDialog] = useState(false);

  // Autosave state
  const [showAutosaveNotification, setShowAutosaveNotification] = useState<boolean>(false);
  const [autosaveMessage, setAutosaveMessage] = useState<string>('');
  const [lastAutosaveTime, setLastAutosaveTime] = useState<Date | null>(null);
  const [showFishBoneDiagram, setShowFishBoneDiagram] = useState<boolean>(false);

  // Ref to track last saved formData for change detection
  const lastSavedFormDataRef = useRef<ProjectFormData | null>(null);

  // Refs to track current state values for autosave (avoid stale closure)
  const isEditModeRef = useRef(isEditMode);
  const projectIdRef = useRef(projectId);
  const isSubmittingRef = useRef(isSubmitting);
  const programmeAreaRef = useRef(formData.programmeArea);

  // Update refs when state changes
  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  useEffect(() => {
    programmeAreaRef.current = formData.programmeArea;
  }, [formData.programmeArea]);

  // Debug: Log isSubmitting state whenever it changes
  useEffect(() => {
  
  }, [isSubmitting]);

  // Check if a project was passed for editing
  useEffect(() => {
    const projectToEdit = location.state?.projectToEdit;
    if (projectToEdit) {
      setIsEditMode(true);
      setProjectId(projectToEdit._id || projectToEdit.id);

      // Convert the Project type to ProjectFormData
      const editData: ProjectFormData = {
        ...initialFormData,
        ...projectToEdit,
        id: projectToEdit._id || projectToEdit.id || initialFormData.id,
        // Keep these fields from projectToEdit if they exist, otherwise use initialFormData
        chsTeamMembers: projectToEdit.chsTeamMembers || [],
        participants: projectToEdit.participants || [],
        decisionMatrix: projectToEdit.decisionMatrix || [],
        rootCauses: projectToEdit.rootCauses || [],
        // Ensure we have the correct dates
        firstCycleDate: projectToEdit.startDate || projectToEdit.firstCycleDate || '',
        projectEndDate: projectToEdit.endDate || projectToEdit.projectEndDate || '',
        // Set status from project
        status: projectToEdit.status as ProjectStatus || 'ongoing',
        // Preserve existing timestamps
        createdAt: projectToEdit.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Restore guided problem statement parts, or parse them back from the composed statement
      editData.problemStatementParts =
        projectToEdit.problemStatementParts || parseProblemStatement(editData.problemStatement);

      // Handle root causes prioritization matrix - convert from backend format to form format
      if (projectToEdit.prioritizedRootCauses && Array.isArray(projectToEdit.prioritizedRootCauses)) {
        
        editData.rootCauseMatrix = projectToEdit.prioritizedRootCauses.map((item: any) => ({
          rootCause: item.cause || '',
          urgency: item.urgency || 1, // Use 1 as minimum default value
          cost: item.cost || 1, // Use 1 as minimum default value
          feasibility: item.feasibility || 1, // Use 1 as minimum default value
          total: (item.total || 0) || (item.urgency || 1) + (item.cost || 1) + (item.feasibility || 1),
          rank: item.rank
        }));
      }

      // Handle action plan items - convert from backend format to form format
      if (projectToEdit.actionPlanItems && Array.isArray(projectToEdit.actionPlanItems)) {
       
        editData.actionPlan = projectToEdit.actionPlanItems.map((item: any) => ({
          action: item.action || '',
          responsiblePerson: item.responsiblePerson || '',
          resources: item.resources || '',
          byWhen: item.byWhen || '',
          place: item.place || '',
          mov: item.mov || ''
        }));
      }

      // Handle decision matrix / problem rankings - convert from backend format to form format
      if (projectToEdit.decisionMatrixItems && Array.isArray(projectToEdit.decisionMatrixItems)) {
        editData.problemRankings = projectToEdit.decisionMatrixItems.map((item: any) => ({
          text: item.gap || item.text || '',
          urgency: item.urgency || 0,
          cost: item.cost || 0,
          impact: item.impact || 0,
          feasibility: item.feasibility || 0,
          total: item.total || 0,
          rank: item.rank || 0
        }));
      } else if (projectToEdit.problemRankings && Array.isArray(projectToEdit.problemRankings)) {
        editData.problemRankings = projectToEdit.problemRankings;
      }

      // Handle identified problems
      if (projectToEdit.identifiedProblems && Array.isArray(projectToEdit.identifiedProblems)) {
        editData.identifiedProblems = projectToEdit.identifiedProblems;
      }

      // Handle KPI/Indicator fields
      editData.useCustomIndicator = projectToEdit.useCustomIndicator || false;
      editData.selectedKPI = projectToEdit.selectedKPI || '';
      editData.indicatorDescription = projectToEdit.indicatorDescription || '';
      editData.indicatorNumerator = projectToEdit.indicatorNumerator || '';
      editData.indicatorDenominator = projectToEdit.indicatorDenominator || '';
      editData.numeratorNumber = projectToEdit.numeratorNumber;
      editData.denominatorNumber = projectToEdit.denominatorNumber;
      editData.improvementFrom = projectToEdit.improvementFrom || 0;
      editData.improvementTo = projectToEdit.improvementTo || 100;

      // Handle other important fields
      editData.domain = projectToEdit.domain || '';
      editData.subDomain = projectToEdit.subDomain || '';
      editData.problemIdentificationTool = projectToEdit.problemIdentificationTool || 'decisionMatrix';
      editData.rootCauseAnalysisTool = projectToEdit.rootCauseAnalysisTool || 'fishbone';
      editData.projectCategory = projectToEdit.projectCategory || '';
      editData.initiativeSource = projectToEdit.initiativeSource || 'CHS';
      editData.responsiblePerson = projectToEdit.responsiblePerson || '';

      // Log what was loaded for editing
  
      // Handle fishbone causes - convert from backend arrays back to form structure
      if (projectToEdit.rootCauseAnalysisTool === 'fishbone') {
        const peopleCauses = (projectToEdit as any).peopleCauses || [];
        const processCauses = (projectToEdit as any).processCauses || [];
        const policyCauses = (projectToEdit as any).policyCauses || [];
        const placeCauses = (projectToEdit as any).placeCauses || [];
        const provisionCauses = (projectToEdit as any).provisionCauses || [];

        // Convert backend arrays to form structure
        editData.fishBoneAnalysis = {
          peopleCauses: {
            causes: '',
            selectedCause: '',
            explanation: '',
            whyAnalysis: { why1: '', reason1: '', why2: '', reason2: '', why3: '', reason3: '', why4: '', reason4: '', why5: '', reason5: '', rootCause: '' },
            causeItems: peopleCauses.map((cause: any) => ({
              id: cause.id || crypto.randomUUID(),
              text: cause.text || cause.cause1 || '',
              impact: cause.impact || 0,
              evidence: cause.evidence || 0,
              feasibility: cause.feasibility || 0,
              total: cause.total || 0,
              rank: cause.rank || 0,
              whyAnalysis: cause.rank === 1 && cause.why1 ? {
                why1: cause.why1 || '',
                why2: cause.why2 || '',
                why3: cause.why3 || '',
                why4: cause.why4 || '',
                why5: cause.why5 || '',
                rootCause: cause.rootCause || ''
              } : undefined
            }))
          },
          processCauses: {
            causes: '',
            selectedCause: '',
            explanation: '',
            whyAnalysis: { why1: '', reason1: '', why2: '', reason2: '', why3: '', reason3: '', why4: '', reason4: '', why5: '', reason5: '', rootCause: '' },
            causeItems: processCauses.map((cause: any) => ({
              id: cause.id || crypto.randomUUID(),
              text: cause.text || cause.cause1 || '',
              impact: cause.impact || 0,
              evidence: cause.evidence || 0,
              feasibility: cause.feasibility || 0,
              total: cause.total || 0,
              rank: cause.rank || 0,
              whyAnalysis: cause.rank === 1 && cause.why1 ? {
                why1: cause.why1 || '',
                why2: cause.why2 || '',
                why3: cause.why3 || '',
                why4: cause.why4 || '',
                why5: cause.why5 || '',
                rootCause: cause.rootCause || ''
              } : undefined
            }))
          },
          policyCauses: {
            causes: '',
            selectedCause: '',
            explanation: '',
            whyAnalysis: { why1: '', reason1: '', why2: '', reason2: '', why3: '', reason3: '', why4: '', reason4: '', why5: '', reason5: '', rootCause: '' },
            causeItems: policyCauses.map((cause: any) => ({
              id: cause.id || crypto.randomUUID(),
              text: cause.text || cause.cause1 || '',
              impact: cause.impact || 0,
              evidence: cause.evidence || 0,
              feasibility: cause.feasibility || 0,
              total: cause.total || 0,
              rank: cause.rank || 0,
              whyAnalysis: cause.rank === 1 && cause.why1 ? {
                why1: cause.why1 || '',
                why2: cause.why2 || '',
                why3: cause.why3 || '',
                why4: cause.why4 || '',
                why5: cause.why5 || '',
                rootCause: cause.rootCause || ''
              } : undefined
            }))
          },
          placeCauses: {
            causes: '',
            selectedCause: '',
            explanation: '',
            whyAnalysis: { why1: '', reason1: '', why2: '', reason2: '', why3: '', reason3: '', why4: '', reason4: '', why5: '', reason5: '', rootCause: '' },
            causeItems: placeCauses.map((cause: any) => ({
              id: cause.id || crypto.randomUUID(),
              text: cause.text || cause.cause1 || '',
              impact: cause.impact || 0,
              evidence: cause.evidence || 0,
              feasibility: cause.feasibility || 0,
              total: cause.total || 0,
              rank: cause.rank || 0,
              whyAnalysis: cause.rank === 1 && cause.why1 ? {
                why1: cause.why1 || '',
                why2: cause.why2 || '',
                why3: cause.why3 || '',
                why4: cause.why4 || '',
                why5: cause.why5 || '',
                rootCause: cause.rootCause || ''
              } : undefined
            }))
          },
          provisionCauses: {
            causes: '',
            selectedCause: '',
            explanation: '',
            whyAnalysis: { why1: '', reason1: '', why2: '', reason2: '', why3: '', reason3: '', why4: '', reason4: '', why5: '', reason5: '', rootCause: '' },
            causeItems: provisionCauses.map((cause: any) => ({
              id: cause.id || crypto.randomUUID(),
              text: cause.text || cause.cause1 || '',
              impact: cause.impact || 0,
              evidence: cause.evidence || 0,
              feasibility: cause.feasibility || 0,
              total: cause.total || 0,
              rank: cause.rank || 0,
              whyAnalysis: cause.rank === 1 && cause.why1 ? {
                why1: cause.why1 || '',
                why2: cause.why2 || '',
                why3: cause.why3 || '',
                why4: cause.why4 || '',
                why5: cause.why5 || '',
                rootCause: cause.rootCause || ''
              } : undefined
            }))
          }
        };
      }

      // Update form data with the project to edit
      setFormData(editData);
      
    }
  }, [location.state]);

  // Load draft project from URL query param (?draft=ID)
  useEffect(() => {
    const draftId = searchParams.get('draft');
    if (!draftId || location.state?.projectToEdit) return; // Skip if already loading from state

    const loadDraft = async () => {
      try {
        const draftProject = await projectService.getProjectById(draftId);
        if (!draftProject) {
          console.warn('[AddProject] Draft project not found:', draftId);
          setSubmitError('Draft project not found. It may have been deleted.');
          return;
        }


        setIsEditMode(true);
        setProjectId(draftProject._id || draftProject.id);

        // Convert the Project to ProjectFormData and populate the form
        // Start with initialFormData and selectively copy fields from draftProject
        const editData: ProjectFormData = {
          ...initialFormData,
          // Core project fields
          id: draftProject._id || draftProject.id || initialFormData.id,
          projectTitle: draftProject.projectTitle || '',
          domain: draftProject.domain || '',
          subDomain: (draftProject as any).subDomain || '',
          programmeArea: (draftProject as any).programmeArea || 'Community Health Services',
          projectCategory: (draftProject as any).projectCategory || '',
          initiativeSource: (draftProject as any).initiativeSource || 'CHS',
          responsiblePerson: (draftProject as any).responsiblePerson || '',
          problemStatement: (draftProject as any).problemStatement || '',
          // Team members
          chsTeamMembers: (draftProject as any).chsTeamMembers || [],
          participants: draftProject.participants || [],
          // Dates
          firstCycleDate: draftProject.startDate || (draftProject as any).firstCycleDate || '',
          projectEndDate: draftProject.endDate || (draftProject as any).projectEndDate || '',
          // Status and metadata
          status: (draftProject.status as ProjectStatus) || 'draft',
          createdAt: draftProject.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: draftProject.createdBy || '',
          // Analysis tools
          problemIdentificationTool: (draftProject as any).problemIdentificationTool || 'decisionMatrix',
          rootCauseAnalysisTool: (draftProject as any).rootCauseAnalysisTool || 'fishbone',
          measurementFrequency: (draftProject as any).measurementFrequency || 'MONTHLY',
          // Arrays that will be populated below
          decisionMatrix: draftProject.decisionMatrix || [],
          rootCauses: (draftProject as any).rootCauses || [],
          identifiedProblems: [],
          problemRankings: [],
          actionPlan: [],
          rootCauseMatrix: [],
        };

        // Restore guided problem statement parts, or parse them back from the composed statement
        editData.problemStatementParts =
          (draftProject as any).problemStatementParts || parseProblemStatement(editData.problemStatement);

        // Handle action plan items from backend format
        if ((draftProject as any).actionPlanItems && Array.isArray((draftProject as any).actionPlanItems)) {
          editData.actionPlan = (draftProject as any).actionPlanItems.map((item: any) => ({
            action: item.action || '',
            responsiblePerson: item.responsiblePerson || '',
            resources: item.resources || '',
            byWhen: item.byWhen || '',
            place: item.place || '',
            mov: item.mov || ''
          }));
        }

        // Handle decision matrix from backend format
        if ((draftProject as any).decisionMatrixItems && Array.isArray((draftProject as any).decisionMatrixItems)) {
          
          editData.problemRankings = (draftProject as any).decisionMatrixItems.map((item: any, index: number) => ({
            id: item.id || item._id || `ranking-${index}`,
            text: item.gap || item.text || '',
            urgency: item.urgency || 0,
            cost: item.cost || 0,
            impact: item.impact || 0,
            feasibility: item.feasibility || 0,
            total: item.total || 0,
            rank: item.rank || 0
          }));
          
          // CRITICAL FIX: Extract identified problems from decisionMatrixItems if identifiedProblems is missing
          if (!(draftProject as any).identifiedProblems || (draftProject as any).identifiedProblems.length === 0) {
            editData.identifiedProblems = (draftProject as any).decisionMatrixItems
              .map((item: any) => item.gap || item.text || '')
              .filter((p: string) => p && p.trim() !== '');
          }
        } else if ((draftProject as any).problemRankings && Array.isArray((draftProject as any).problemRankings)) {
      
          editData.problemRankings = (draftProject as any).problemRankings.map((item: any, index: number) => ({
            id: item.id || `ranking-${index}`,
            text: item.text || '',
            urgency: item.urgency || 0,
            cost: item.cost || 0,
            impact: item.impact || 0,
            feasibility: item.feasibility || 0,
            total: item.total || 0,
            rank: item.rank || 0
          }));
          
          // Extract identified problems from problemRankings if identifiedProblems is missing
          if (!(draftProject as any).identifiedProblems || (draftProject as any).identifiedProblems.length === 0) {
            editData.identifiedProblems = (draftProject as any).problemRankings
              .map((item: any) => item.text || '')
              .filter((p: string) => p && p.trim() !== '');
          }
        }

        // Handle identified problems
        if ((draftProject as any).identifiedProblems && Array.isArray((draftProject as any).identifiedProblems)) {
          editData.identifiedProblems = (draftProject as any).identifiedProblems.filter((p: string) => p && p.trim() !== '');
        }
        
        // Ensure at least one empty field if no problems exist
        if (!editData.identifiedProblems || editData.identifiedProblems.length === 0) {
          editData.identifiedProblems = [''];
        }

        // Handle KPI/Indicator fields
        editData.useCustomIndicator = (draftProject as any).useCustomIndicator || false;
        editData.selectedKPI = (draftProject as any).selectedKPI || '';
        editData.indicatorDescription = (draftProject as any).indicatorDescription || '';
        editData.indicatorNumerator = (draftProject as any).indicatorNumerator || '';
        editData.indicatorDenominator = (draftProject as any).indicatorDenominator || '';
        editData.numeratorNumber = (draftProject as any).numeratorNumber;
        editData.denominatorNumber = (draftProject as any).denominatorNumber;
        editData.improvementFrom = (draftProject as any).improvementFrom || 0;
        editData.improvementTo = (draftProject as any).improvementTo || 100;
        editData.customKpiDefinition = (draftProject as any).customKpiDefinition || '';

        // Handle root cause matrix
        if ((draftProject as any).prioritizedRootCauses && Array.isArray((draftProject as any).prioritizedRootCauses)) {
          editData.rootCauseMatrix = (draftProject as any).prioritizedRootCauses.map((item: any) => ({
            rootCause: item.cause || '',
            urgency: item.urgency || 1,
            cost: item.cost || 1,
            feasibility: item.feasibility || 1,
            total: (item.total || 0) || (item.urgency || 1) + (item.cost || 1) + (item.feasibility || 1),
            rank: item.rank
          }));
        }

        // Handle fishbone causes
        if ((draftProject as any).rootCauseAnalysisTool === 'fishbone') {
          const peopleCauses = (draftProject as any).peopleCauses || [];
          const processCauses = (draftProject as any).processCauses || [];
          const policyCauses = (draftProject as any).policyCauses || [];
          const placeCauses = (draftProject as any).placeCauses || [];
          const provisionCauses = (draftProject as any).provisionCauses || [];

          const mapCauses = (causes: any[]) => causes.map((cause: any) => ({
            id: cause.id || crypto.randomUUID(),
            text: cause.text || cause.cause1 || '',
            impact: cause.impact || 0,
            evidence: cause.evidence || 0,
            feasibility: cause.feasibility || 0,
            total: cause.total || 0,
            rank: cause.rank || 0,
            whyAnalysis: cause.rank === 1 && cause.why1 ? {
              why1: cause.why1 || '', why2: cause.why2 || '', why3: cause.why3 || '',
              why4: cause.why4 || '', why5: cause.why5 || '', rootCause: cause.rootCause || ''
            } : undefined
          }));

          const emptyWhyAnalysis = { why1: '', reason1: '', why2: '', reason2: '', why3: '', reason3: '', why4: '', reason4: '', why5: '', reason5: '', rootCause: '' };
          editData.fishBoneAnalysis = {
            peopleCauses: { causes: '', selectedCause: '', explanation: '', whyAnalysis: { ...emptyWhyAnalysis }, causeItems: mapCauses(peopleCauses) },
            processCauses: { causes: '', selectedCause: '', explanation: '', whyAnalysis: { ...emptyWhyAnalysis }, causeItems: mapCauses(processCauses) },
            policyCauses: { causes: '', selectedCause: '', explanation: '', whyAnalysis: { ...emptyWhyAnalysis }, causeItems: mapCauses(policyCauses) },
            placeCauses: { causes: '', selectedCause: '', explanation: '', whyAnalysis: { ...emptyWhyAnalysis }, causeItems: mapCauses(placeCauses) },
            provisionCauses: { causes: '', selectedCause: '', explanation: '', whyAnalysis: { ...emptyWhyAnalysis }, causeItems: mapCauses(provisionCauses) },
          };
        }

        setFormData(editData);
      } catch (error) {
        console.error('[AddProject] Error loading draft project:', error);
        setSubmitError('Failed to load draft project. Please try again.');
      }
    };

    loadDraft();
  }, [searchParams, location.state]);

  // Local validation function
  const validateFormDataLocal = (): boolean => {
   
    
    // Use centralized form validation
    const formErrors = validateFormData(formData);
    
    // Use centralized team member validation
    const chsTeamMembers = formData.chsTeamMembers || [];
    const teamErrors = validateTeamMembers(chsTeamMembers);
    
    // Use centralized participant validation
    const participants = formData.participants || [];
    const participantErrors = validateParticipants(participants);
    
    // Set the errors in state
    setErrors(formErrors);
    setTeamMemberErrors(teamErrors);
    setParticipantErrors(participantErrors);
    
    // Check if there are any errors
    const hasFormErrors = Object.keys(formErrors).length > 0;
    const hasTeamErrors = Object.keys(teamErrors).length > 0;
    const hasParticipantErrors = Object.keys(participantErrors).length > 0;
    
    const hasErrors = hasFormErrors || hasTeamErrors || hasParticipantErrors;
    
    
    if (hasErrors) {
     
    }
    
    return !hasErrors;
  };

  // Handle dropdown selection changes
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field if any
    if (errors[name as keyof ProjectFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Special handling for domain selection in Community Health Services
    if (name === 'domain') {
      // When domain changes, clear the subDomain and set a new one if available
      setFormData((prev) => ({
        ...prev,
        subDomain: CHS_SUBDOMAINS[value]?.[0] || '',
      }));
    }
  };

  const handleParticipantChange = (index: number, field: keyof Participant, value: string) => {
    setFormData((prev) => {
      const updatedParticipants = [...prev.participants];
      updatedParticipants[index] = { ...updatedParticipants[index], [field]: value };
      return { ...prev, participants: updatedParticipants };
    });

    // Clear error for this field if any
    if (participantErrors[index]?.[field]) {
      setParticipantErrors((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: '',
        },
      }));
    }
  };

  const addParticipant = () => {
    // Check limit before attempting to add
    if (formData.participants.length >= 10) {
      
      return; // Exit early if limit reached
    }

    setFormData((prev) => ({
      ...prev,
      participants: [...prev.participants, { ...initialParticipant }]
    }));
  };

  const removeParticipant = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index),
    }));
  };

  // Guided problem statement: update one part and recompose the full statement
  const handleProblemPartChange =
    (field: keyof ProblemStatementParts) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => {
        const parts = {
          ...(prev.problemStatementParts || initialProblemStatementParts),
          [field]: value
        };
        return {
          ...prev,
          problemStatementParts: parts,
          problemStatement: composeProblemStatement(parts)
        };
      });

      if (errors.problemStatement) {
        setErrors(prev => ({ ...prev, problemStatement: '' }));
      }
    };

  // Generic input change handler for text fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Special validation for projectTitle, responsiblePerson and problemStatement
    if (name === 'projectTitle' || name === 'responsiblePerson' || name === 'problemStatement') {
      // For problemStatement, disallow numbers-only input
      if (name === 'problemStatement' && /^[0-9]+$/.test(value) && value !== '') {
        // If it's numbers-only and not empty, update the error state but don't update the form data
        setErrors(prev => ({
          ...prev,
          problemStatement: 'Problem statement cannot contain only numbers'
        }));
     
        return;
      }
      // For all fields in this group, check if they contain at least one letter
      if (/[A-Za-z]/.test(value) || value === '') {
        // If it contains letters or is empty, allow the input
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));

        // Clear error for problemStatement if it was previously set
        if (name === 'problemStatement' && errors.problemStatement === 'Problem statement cannot contain only numbers') {
          setErrors(prev => ({
            ...prev,
            problemStatement: ''
          }));
        }
      } else {
        // If it's numbers-only, don't update the form data
        
      }
    } else {
      // For numeric fields, convert to number
      if (name === 'improvementFrom' || name === 'improvementTo') {
        const numericValue = value === '' ? undefined : Number(value);
        setFormData(prev => ({
          ...prev,
          [name]: numericValue
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };

  // Handler for 'Save as Draft' button
  const handleSaveAsDraft = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    setSubmitError('');

    try {
      // Get the finalized project data with all required fields (no validation for drafts)
      const finalProjectData = checkRequiredFields(formData);

      let savedProject;

      if (isEditMode && projectId) {
        // Updating an existing draft — use projectService.updateProject (includes auth)

        const draftData: Partial<Project> = {
          ...finalProjectData,
          status: 'draft' as any,
        };
        savedProject = await projectService.updateProject(projectId, draftData);
      } else {
        // Creating a new draft
        savedProject = await projectService.saveProjectAsDraft(finalProjectData);
      }

      // Store in localStorage for reference
      localStorage.setItem('lastSavedDraft', JSON.stringify(savedProject));

      // Stay on the form — show success message and switch to edit mode for new drafts
      const newId = savedProject._id || savedProject.id;
      if (!isEditMode && newId) {
        // New draft was created — switch to edit mode so future saves update the same draft
        setIsEditMode(true);
        setProjectId(newId);
        // Update URL to include draft ID without full reload
        window.history.replaceState(null, '', `/add-project?draft=${newId}`);
      }

      setSubmitError(''); // clear any previous errors
      // Show a brief success banner using submitError field (repurposed for success)
      setDraftSuccessMessage(isEditMode ? 'Draft updated successfully!' : 'Draft saved successfully!');
      setTimeout(() => setDraftSuccessMessage(''), 3000);
    } catch (error) {
      console.error('[AddProject] Failed to save draft:', error);
      setSubmitError('Failed to save draft. Please try again.');

      // Scroll error into view
      const errorElement = document.querySelector('#submit-error');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth' });
      }
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Autosave function - saves draft silently every 15 minutes
  const handleAutosave = async () => {
   
    
    // Don't autosave if already submitting or if form is completely empty
    if (isSubmittingRef.current || !programmeAreaRef.current) {

      return;
    }

    try {
      
      // Get the finalized project data
      const finalProjectData = checkRequiredFields(formData);

      // Check if there are changes since last save
      const hasChanges = !lastSavedFormDataRef.current || 
        JSON.stringify(finalProjectData) !== JSON.stringify(lastSavedFormDataRef.current);
      
      if (!hasChanges) {
        return;
      }

      let savedProject;

      if (isEditModeRef.current && projectIdRef.current) {
        // Update existing draft
        const draftData: Partial<Project> = {
          ...finalProjectData,
          status: 'draft' as any,
        };
        
        savedProject = await projectService.updateProject(projectIdRef.current, draftData);
      } else {
        // Create new draft
        savedProject = await projectService.saveProjectAsDraft(finalProjectData);
        
        // Switch to edit mode for future autosaves
        const newId = savedProject._id || savedProject.id;
        if (newId) {
          setIsEditMode(true);
          setProjectId(newId);
          window.history.replaceState(null, '', `/add-project?draft=${newId}`);
        }
      }

      // Update last saved formData ref
      lastSavedFormDataRef.current = finalProjectData;

      // Update last autosave time
      const now = new Date();
      setLastAutosaveTime(now);
      
      // Show autosave notification
      const message = `Draft auto-saved at ${now.toLocaleTimeString()}`;
      setAutosaveMessage(message);
      setShowAutosaveNotification(true);
  
    } catch (error) {
      console.error('[Autosave] Failed to autosave:', error);
      // Don't show error to user for autosave failures - it's silent
    }
  };

  // Set up autosave to trigger on form data changes
  useEffect(() => {
    let debounceTimer: NodeJS.Timeout;

    const handleFormDataChange = () => {
      // Clear previous timer
      clearTimeout(debounceTimer);

      // Set new timer to autosave after 30 seconds of inactivity
      debounceTimer = setTimeout(() => {
        
        handleAutosave();
      }, 30 * 1000); // 30 seconds debounce
    };

    // Watch for formData changes
    handleFormDataChange();

    // Cleanup timer on unmount or when formData changes
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [formData]); // Re-run when formData changes

  // Keep 15-minute interval as fallback for long sessions without changes
  useEffect(() => {
    
    const autosaveInterval = setInterval(() => {
      handleAutosave();
    }, 15 * 60 * 1000); // 15 minutes

    // Cleanup interval on unmount
    return () => {

      clearInterval(autosaveInterval);
    };
  }, []);




  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    setSubmitError('');

    // Add a timestamp to track how long submission takes
    const startTime = new Date().getTime();

    // Set a guaranteed escape hatch timer - 20 seconds max for submission
    const escapeHatchTimer = setTimeout(() => {
      

      // Create a minimal backup project to use for navigation
      const backupProject = {
        id: `local_emergency_${Date.now()}`,
        _id: `local_emergency_${Date.now()}`,
        projectTitle: formData.projectTitle || 'Untitled Project',
        status: 'ongoing'
      };

      // Save the form data to localStorage for recovery
      localStorage.setItem('emergency_backup_project', JSON.stringify(formData));
      localStorage.setItem('lastCreatedProject', JSON.stringify(backupProject));

      // Force navigation to projects list
      
      window.location.href = '/projects/ongoing';
    }, 20000); // 20 seconds max

    try {
      // Validate form data first
      
      const isValid = validateFormDataLocal();
      

      if (!isValid) {
        
        setIsSubmitting(false);
        // Scroll to first error
        scrollToFirstError();
        clearTimeout(escapeHatchTimer);
        return;
      }

      

      // Get the finalized project data with all required fields
      
      let finalProjectData;
      try {
        // Modify form data to ensure status is 'ongoing' before generating final data
        setFormData(prev => ({
          ...prev,
          status: 'ongoing' as ProjectStatus
        }));

        // Create a direct copy with status set to avoid async state issues
        const updatedFormData = {
          ...formData,
          status: 'ongoing' as ProjectStatus
        };

    
        // Use the updated form data directly
        finalProjectData = checkRequiredFields(formData, updatedFormData);
        
        
  
        
        // Missing Fields Check
        const formDataKeys = Object.keys(formData);
        const finalDataKeys = Object.keys(finalProjectData);
        const missingFields = formDataKeys.filter(key => {
          const formValue = (formData as any)[key];
          return formValue !== undefined && 
                 formValue !== null && 
                 formValue !== '' && 
                 !finalDataKeys.includes(key);
        });
  
        
      } catch (requiredFieldsError) {
        
        setSubmitError('Error preparing form data. Please check all required fields.');
        setIsSubmitting(false);
        clearTimeout(escapeHatchTimer);
        return;
      }
      

      // CRITICAL: Force status to 'ongoing' for new projects AND drafts being submitted
      if (!isEditMode || finalProjectData.status === 'draft') {
        
        finalProjectData.status = 'ongoing' as ProjectStatus;
      } else if (finalProjectData.status === 'completed') {
        // For completed projects, API expects 'past_completion'
       
        finalProjectData.status = 'past_completion' as ProjectStatus;
      }

      // Double verification of status to ensure it's set correctly for API
      

      // Import API config if not already imported
      const { ApiConfig } = require('../services/api');
      

      // Verify connection to API before attempting to create/update project
      try {
        const testResponse = await fetch(`${ApiConfig.baseUrl}/projects`, {
          method: 'GET',
          headers: ApiConfig.defaultHeaders
        });

        if (testResponse.ok) {
          
        } else {
         
        }
      } catch (connectionError) {
      
      }

      if (isEditMode && projectId) {
        // Update existing project
       
        try {
          // Convert ProjectFormData to Project type for API compatibility
          const projectData: Partial<Project> = {
            ...finalProjectData,
            // Force status as a valid ProjectStatus without undefined
            // Ensure we only use valid ProjectStatus values
            status: (() => {
              // Only proceed if we have a status value
              if (!finalProjectData.status) return undefined;

              // Handle completed projects - map to past_completion
              if (finalProjectData.status === 'completed') {
                return 'past_completion' as const;
              }

              // Handle draft submissions (API expects 'ongoing' not 'draft')
              if (finalProjectData.status === 'draft') {
                return 'ongoing' as const;
              }

              // For other cases, use the existing status
              return finalProjectData.status as any;
            })(),
            // Ensure date field names match the Project interface
            startDate: finalProjectData.firstCycleDate,
            endDate: finalProjectData.projectEndDate,
            // Update the timestamp
            updatedAt: new Date().toISOString(),
            // CRITICAL FIX: Explicitly include decision matrix data
            decisionMatrix: finalProjectData.decisionMatrix,
            // CRITICAL FIX: Explicitly include fishbone transformed arrays
            peopleCauses: finalProjectData.peopleCauses,
            processCauses: finalProjectData.processCauses,
            policyCauses: finalProjectData.policyCauses,
            placeCauses: finalProjectData.placeCauses,
            provisionCauses: finalProjectData.provisionCauses,
            fishBoneAnalysis: finalProjectData.fishBoneAnalysis,
            showFishBoneDiagram: finalProjectData.showFishBoneDiagram
          };

          const updatedProject = await projectService.updateProject(projectId, projectData);
          

          // If we get here, the project was updated successfully
          localStorage.setItem('lastUpdatedProject', JSON.stringify(updatedProject));

          // Navigate to the projects list with success flag instead of project detail
          
          navigate('/projects/ongoing', {
            state: {
              projectUpdated: true,
              project: updatedProject
            },
            replace: true // Replace current history entry to prevent back navigation to form
          });
          return; // Exit function after successful update
        } catch (updateError) {
          
          setSubmitError('Failed to update project. Please try again.');
          setIsSubmitting(false);

          // Scroll error into view
          const errorElement = document.querySelector('#submit-error');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth' });
          }
          return; // Exit function after error
        }
      } else {
        // Create new project
        try {
          let createdProject;

          try {
            // Add timeout to prevent infinite waiting
            const timeout = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('API call timeout after 15 seconds')), 15000);
            });

            // Race between the API call and the timeout
            createdProject = await Promise.race([
              projectService.createProject(finalProjectData),
              timeout
            ]);

            // If we get here, the project was created successfully
            localStorage.setItem('lastCreatedProject', JSON.stringify(createdProject));

            // Navigate to the ongoing projects page with success flag
            try {
              // Use navigate function with explicit path and state to ensure proper routing
              // First attempt with React Router navigate - with explicit refresh flag
              navigate('/projects/ongoing', {
                state: {
                  projectCreated: true,
                  project: createdProject,
                  refreshProjects: true, // Force a refresh of projects
                  message: 'Project created successfully!' // Show success message
                },
                replace: true // Replace current history entry to prevent back navigation to form
              });
             

              // Force a direct navigation after a short delay as a backup
              // This ensures navigation happens even if there's an issue with React Router
              setTimeout(() => {
               
                window.location.href = '/projects/ongoing?refresh=true';
              }, 1000);
            } catch (navError) {
              
              // Immediate fallback to direct URL change if React Router fails
              window.location.href = '/projects/ongoing';
            }
            return; // Exit function after successful creation

          } catch (projectApiError) {
            
            // Continue to direct API call - will be caught in outer catch block
            throw projectApiError;
          }

        } catch (createError) {
          

          // Try direct API call as fallback using ApiConfig rather than hardcoded URL
          
          try {
            // Import API config and adapters
            const { ApiConfig } = require('../services/api');
            const { adaptProjectForApi } = require('../services/adapters');

            // Create a deep copy of the data to ensure no reference issues
            const projectCopy = JSON.parse(JSON.stringify(finalProjectData));

            // Force status to 'ongoing' again for direct API call
            projectCopy.status = 'ongoing';
            

            // Use the adapter to prepare data for API
            const apiPayload = adaptProjectForApi(projectCopy);

            

            // Set fetch timeout to avoid hanging forever
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

            // Use the AbortController to prevent hanging indefinitely
            const directResponse = await fetch(`${ApiConfig.baseUrl}/projects`, {
              method: 'POST',
              headers: ApiConfig.defaultHeaders,
              body: JSON.stringify(apiPayload),
              signal: controller.signal
            }).finally(() => {
              // Clear the timeout to avoid memory leaks
              clearTimeout(timeoutId);
            });

            // Log response details for debugging
           

            // Log response headers
            const headers: Record<string, string> = {};
            directResponse.headers.forEach((value, key) => {
              headers[key] = value;
            });
            
            if (directResponse.ok) {
              const data = await directResponse.json();
             

              localStorage.setItem('lastCreatedProject', JSON.stringify(data));

             
              try {
                // Use enhanced navigation with replace flag
                navigate('/projects/ongoing', {
                  state: {
                    projectCreated: true,
                    project: data
                  },
                  replace: true // Replace current history entry to prevent back navigation
                });
                
              } catch (navError) {
                
                // Fallback navigation using window.location
                window.location.href = '/projects/ongoing';
              }
              return; // Exit function after successful creation
            } else {
              
              setSubmitError('Failed to create project. Please try again.');
              setIsSubmitting(false);

              // Scroll error into view if element exists
              const errorElement = document.querySelector('#submit-error');
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth' });
              }
            }
          } catch (directApiError) {
            
            setSubmitError('Failed to connect to server. Please check your connection and try again.');
            setIsSubmitting(false);

            // Scroll error into view if element exists
            const errorElement = document.querySelector('#submit-error');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }
      }
    } catch (error) {
     
      setSubmitError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);

      // Scroll error into view if element exists
      const errorElement = document.querySelector('#submit-error');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth' });
      }
    } finally {
      // Make sure we clear the emergency escape hatch timer
      clearTimeout(escapeHatchTimer);

      // Always ensure submission state is reset
      setIsSubmitting(false);

      // Log total submission time
      const endTime = new Date().getTime();
     
    }
  };

  // Debug: Log when component renders
  
  // Handle back button - save as draft
  const handleBackWithSave = async () => {
    setShowBackDialog(false);

    // Check if there are actual changes before saving
    const finalProjectData = checkRequiredFields(formData);
    const hasChanges =
      !lastSavedFormDataRef.current ||
      JSON.stringify(finalProjectData) !== JSON.stringify(lastSavedFormDataRef.current);

    if (hasChanges) {
      await handleSaveAsDraft();
    } 

    navigate(-1);
  };

  // Handle back button - don't save
  const handleBackWithoutSave = () => {
    setShowBackDialog(false);
    navigate(-1);
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <TopBar onLogout={onLogout} />
      </Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => setShowBackDialog(true)} color="primary">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" gutterBottom>Add New QI Project</Typography>
          </Box>
          <Breadcrumbs sx={{ mb: 4 }}>
            <Link href="/" color="inherit">
              Dashboard
            </Link>
            <Typography color="text.primary">Add New QI Project</Typography>
          </Breadcrumbs>

          {draftSuccessMessage && (
        <Box sx={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          <Alert severity="success" onClose={() => setDraftSuccessMessage('')}>
            {draftSuccessMessage}
          </Alert>
        </Box>
      )}

      {submitError && (
            <Typography color="error" sx={{ mb: 2 }} id="submit-error">
              {submitError}
            </Typography>
          )}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3} sx={{ width: '100%' }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Programme Area"
                  name="programmeArea"
                  value="Community Health Services"
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>

              {/* List of Identified Problems to be addressed */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  List of Identified Problems to be addressed
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  List all gaps identified in the KQMCH Assessment, along with any other community problems in line with CHS noted. Maximum 3 problems allowed.
                </Typography>
                
                {(formData.identifiedProblems || []).map((problem, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                      fullWidth
                      label={`Problem ${index + 1}`}
                      value={problem}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Validation: no numbers only
                        const isNumbersOnly = /^\d+$/.test(value.trim());
                        
                        if (!isNumbersOnly || value.trim() === '') {
                          const updatedProblems = [...(formData.identifiedProblems || [])];
                          updatedProblems[index] = value;
                          setFormData(prev => ({
                            ...prev,
                            identifiedProblems: updatedProblems
                          }));
                        }
                      }}
                      error={/^\d+$/.test(problem.trim()) && problem.trim() !== ''}
                      helperText={/^\d+$/.test(problem.trim()) && problem.trim() !== '' ? 'Cannot contain numbers only' : ''}
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => {
                        const updatedProblems = (formData.identifiedProblems || []).filter((_, i) => i !== index);
                        setFormData(prev => ({
                          ...prev,
                          identifiedProblems: updatedProblems
                        }));
                      }}
                      disabled={(formData.identifiedProblems || []).length <= 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={() => {
                    const currentProblems = formData.identifiedProblems || [];
                    if (currentProblems.length < 3) {
                      setFormData(prev => ({
                        ...prev,
                        identifiedProblems: [...currentProblems, '']
                      }));
                    }
                  }}
                  disabled={(formData.identifiedProblems || []).length >= 3}
                  sx={{ mb: 2 }}
                >
                  Add Problem
                </Button>

                {(formData.identifiedProblems || []).length >= 3 && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                    Maximum 3 problems allowed
                  </Typography>
                )}

                {/* Ranking Component for Identified Problems */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    Decision Matrix for identified Problem Prioritization
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Fill in the Decision Matrix below to prioritize the identified gaps
                  </Typography>
                </Box>
                <RankingComponent
                  title=""
                  itemColumnTitle="Identified Problems to be addressed"
                  items={(() => {
                    const problems = (formData.identifiedProblems || []).filter(problem => problem.trim() !== '');
                    const existingRankings = formData.problemRankings || [];
                    
                    return problems.map((problem, index) => {
                      const existingRanking = existingRankings.find(r => r.text === problem);
                      return existingRanking || {
                        id: `problem-${index}`,
                        text: problem,
                        urgency: undefined,
                        cost: undefined,
                        feasibility: undefined,
                        total: undefined,
                        rank: undefined
                      };
                    });
                  })()}
                  criteria={[
                    {
                      key: 'urgency',
                      label: 'Urgency',
                      description: 'identified gap needs immediate attention',
                      options: [
                        { value: 3, label: '3 - Very urgent' },
                        { value: 2, label: '2 - Moderately urgent' },
                        { value: 1, label: '1 - Not urgent' }
                      ]
                    },
                    {
                      key: 'cost',
                      label: 'Cost',
                      description: 'financial needs versus availability of a budget to resolve the problem',
                      options: [
                        { value: 3, label: '3 - Low cost' },
                        { value: 2, label: '2 - Moderate cost' },
                        { value: 1, label: '1 - High cost' }
                      ]
                    },
                    {
                      key: 'feasibility',
                      label: 'Feasibility',
                      description: 'how easy is it to do/implement ',
                      options: [
                        { value: 3, label: '3 - Very easy to implement' },
                        { value: 2, label: '2 - Manageable to implement' },
                        { value: 1, label: '1 - Difficult to implement' }
                      ]
                    }
                  ]}
                  onItemsChange={(rankedItems: RankingItem[]) => {
                    setFormData(prev => ({
                      ...prev,
                      problemRankings: rankedItems
                    }));
                  }}
                  emptyMessage="Add problems above to begin ranking them by priority."
                />
                
                {/* Selected Problem Note */}
                {(() => {
                  const rankings = formData.problemRankings || [];
                  const highestRanked = rankings.find(item => item.rank === 1);
                  
                  if (highestRanked && highestRanked.total && highestRanked.total > 0) {
                    return (
                      <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          📌 Selected Problem for QI Project:
                        </Typography>
                        <Typography variant="body2">
                          <strong>{highestRanked.text}</strong> 
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Grid>

              {/* Community Health Services specific fields - displayed conditionally */}
              {formData.programmeArea === 'Community Health Services' && (
                <DomainSubdomainSelector
                  domain={formData.domain || ''}
                  subDomain={formData.subDomain || ''}
                  domains={CHS_DOMAINS}
                  subDomains={CHS_SUBDOMAINS}
                  onDomainChange={handleSelectChange}
                  onSubDomainChange={handleSelectChange}
                  domainHelperText="Select the domain in community health services where the selected problem lies (e.g., Service Delivery, Governance)."
                  subDomainHelperText="Specific component within a domain that breaks it down into actionable focus areas."
                />
              )}

              {/* QI Project Title */}
              <ProjectTitleInput
                value={formData.projectTitle}
                onChange={handleInputChange}
                error={!!errors.projectTitle}
                errorMessage={errors.projectTitle || ""}
              />

              {/* QI Project Lead */}
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                  Name of the person responsible for coordinating and overseeing the QI project.
                </Typography>
                <TextField
                  fullWidth
                  name="responsiblePerson"
                  label="QI Project Lead"
                  value={formData.responsiblePerson}
                  onChange={handleInputChange}
                  error={!!errors.responsiblePerson}
                  helperText={errors.responsiblePerson || ""}
                  required
                />
              </Box>

              {/* Participants List - show only for non-CHS projects */}
              {formData.programmeArea !== 'Community Health Services' && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    QI Project Team Members
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Add up to 10 team members involved in the QI project (current: {formData.participants.length}/10)
                  </Typography>
                  {formData.participants.map((participant, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          value={participant.fullName}
                          onChange={(e) => handleParticipantChange(index, 'fullName', e.target.value)}
                          error={!!participantErrors[index]?.fullName}
                          helperText={participantErrors[index]?.fullName}
                        />
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={participant.email}
                          onChange={(e) => handleParticipantChange(index, 'email', e.target.value)}
                          error={!!participantErrors[index]?.email}
                          helperText={participantErrors[index]?.email}
                        />
                        <TextField
                          fullWidth
                          label="Phone"
                          value={participant.phone}
                          onChange={(e) => handleParticipantChange(index, 'phone', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Role"
                          value={participant.role}
                          onChange={(e) => handleParticipantChange(index, 'role', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton onClick={() => removeParticipant(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                  <Button
                    startIcon={<PersonAddIcon />}
                    onClick={addParticipant}
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 1 }}
                    disabled={formData.participants.length >= 10}
                  >
                    Add Participant
                  </Button>
                </Box>
              )}

              {/* QI Project Team - show only for CHS projects */}
              {formData.programmeArea === 'Community Health Services' && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    QI Project Team
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    List names and roles of {MIN_TEAM_MEMBERS}-{MAX_TEAM_MEMBERS} team members actively involved in the QI project (e.g., CHA, CHP, Supervisor). Current: {(formData.chsTeamMembers || []).length}/{MAX_TEAM_MEMBERS}
                  </Typography>
                  {formData.chsTeamMembers && formData.chsTeamMembers.map((member, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={member.fullName}
                          error={!!member.fullNameError}
                          helperText={member.fullNameError || ""}
                          onChange={(e) => {
                            const updatedMembers = [...(formData.chsTeamMembers || [])];

                            // Validate that name is not numbers-only
                            const value = e.target.value;
                          
                            
                            // Check if the value contains at least one letter or is empty
                            if (/[A-Za-z]/.test(value) || value === '') {
                              updatedMembers[index] = {
                                fullName: value,
                                phoneNumber: updatedMembers[index]?.phoneNumber || '', // EXPLICITLY preserve
                                role: updatedMembers[index]?.role || '',
                                customRole: updatedMembers[index]?.customRole || '',
                                fullNameError: ''
                              };
                            } else {
                              // If numbers-only, keep the previous value and set error
                              updatedMembers[index] = {
                                ...updatedMembers[index],
                                fullNameError: 'Name must contain at least one letter'
                              };
                             
                            }
                            

                            setFormData(prev => ({
                              ...prev,
                              chsTeamMembers: updatedMembers
                            }));
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Phone Number"
                          value={member.phoneNumber || ''}
                          error={Boolean(teamMemberErrors[index]?.phoneNumber)}
                          helperText={teamMemberErrors[index]?.phoneNumber || ''}
                          onChange={(e) => {
                            // Only allow numeric input
                            const value = e.target.value;
                            const numericValue = value.replace(/[^0-9]/g, '');
                            const updatedMembers = [...(formData.chsTeamMembers || [])]

                            const setPhoneError = (message: string | null) => {
                              setTeamMemberErrors(prev => {
                                const updated = { ...prev };
                                if (message) {
                                  updated[index] = { ...updated[index], phoneNumber: message };
                                } else if (updated[index]?.phoneNumber) {
                                  updated[index] = { ...updated[index] };
                                  delete updated[index].phoneNumber;
                                }
                                return updated;
                              });
                            };

                            // Validate phone: digits only, exactly 10, and unique across members
                            if (value !== numericValue || numericValue.length > 10) {
                              setPhoneError(PHONE_LENGTH_MESSAGE);
                            } else if (numericValue.length === 10 && updatedMembers.some((m, i) => i !== index && m.phoneNumber === numericValue)) {
                              setPhoneError(PHONE_DUPLICATE_MESSAGE);
                            } else {
                              setPhoneError(null);
                            }

                            updatedMembers[index] = {
                              ...updatedMembers[index],
                              phoneNumber: numericValue
                            };
                            
                            
                            setFormData(prev => ({
                              ...prev,
                              chsTeamMembers: updatedMembers
                            }));
                          }}
                        />
                        <FormControl fullWidth>
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={member.role}
                            onChange={(e) => {
                              const updatedMembers = [...(formData.chsTeamMembers || [])];
                              
                              
                              updatedMembers[index] = {
                                fullName: updatedMembers[index]?.fullName || '',
                                phoneNumber: updatedMembers[index]?.phoneNumber || '', // EXPLICITLY preserve
                                role: e.target.value,
                                // Clear customRole if not Community Member
                                customRole: e.target.value !== 'Community Member' ? '' : (updatedMembers[index]?.customRole || '')
                              };
                              
                              setFormData(prev => ({
                                ...prev,
                                chsTeamMembers: updatedMembers
                              }));
                            }}
                            label="Role"
                          >
                            {CHS_ROLES.map((role) => (
                              <MenuItem key={role} value={role}>
                                {role}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            onClick={() => {
                              const updatedMembers = [...(formData.chsTeamMembers || [])];
                              updatedMembers.splice(index, 1);
                              setFormData(prev => ({
                                ...prev,
                                chsTeamMembers: updatedMembers
                              }));
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Stack>
                      {member.role === 'Community Member' && (
                        <TextField
                          fullWidth
                          label="Specify role (**if community member)"
                          value={member.customRole || ''}
                          onChange={(e) => {
                            const updatedMembers = [...(formData.chsTeamMembers || [])];
                            // Check if input is numbers-only
                            const isNumbersOnly = /^\d+$/.test(e.target.value);

                            updatedMembers[index] = {
                              ...updatedMembers[index],
                              customRole: e.target.value,
                              customRoleError: e.target.value.trim() === ''
                                ? 'Role is required for community members'
                                : isNumbersOnly && e.target.value.trim() !== ''
                                  ? 'Role cannot contain only numbers'
                                  : undefined
                            };
                            setFormData(prev => ({
                              ...prev,
                              chsTeamMembers: updatedMembers
                            }));
                          }}
                          sx={{ mt: 2 }}
                          required
                          error={!!member.customRoleError}
                          helperText={member.customRoleError}
                        />
                      )}
                    </Box>
                  ))}
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                    Click on add team member to list all the QI Project Team involved ({MIN_TEAM_MEMBERS} minimum, {MAX_TEAM_MEMBERS} maximum)
                  </Typography>
                  {errors.chsTeamMembers && (
                    <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                      {errors.chsTeamMembers}
                    </Typography>
                  )}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      // Add new CHS team member
                      const newMember = { ...initialCHSTeamMember };
                      setFormData(prev => ({
                        ...prev,
                        chsTeamMembers: [...(prev.chsTeamMembers || []), newMember]
                      }));
                    }}
                    variant="outlined"
                    sx={{ mt: 1 }}
                    disabled={(formData.chsTeamMembers || []).length >= MAX_TEAM_MEMBERS}
                  >
                    Add Team Member
                  </Button>
                </Box>
              )}



              {/* Problem Statement - guided template, composed and submitted as a whole */}
              <Box sx={{ width: '100%', mt: 3, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Problem Statement
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <em>Fill in the blanks below. The completed sentence will be submitted as the problem statement. It should be simple, precise, quantified, indicate where, time period and consequences if the problem is not addressed.</em>
                </Typography>

                <Paper variant="outlined" sx={{ p: 3, mt: 1, backgroundColor: 'grey.50' }}>
                  <Typography component="div" variant="body1" sx={{ lineHeight: 3 }}>
                    In{' '}
                    <ProblemBlank
                      placeholder="context (place & time period)"
                      width={240}
                      value={formData.problemStatementParts?.context || ''}
                      onChange={handleProblemPartChange('context')}
                    />
                    , the issue is{' '}
                    <ProblemBlank
                      placeholder="the problem"
                      width={240}
                      value={formData.problemStatementParts?.issue || ''}
                      onChange={handleProblemPartChange('issue')}
                    />
                    . Out of{' '}
                    <ProblemBlank
                      placeholder="denominator / expected"
                      width={220}
                      value={formData.problemStatementParts?.denominator || ''}
                      onChange={handleProblemPartChange('denominator')}
                    />
                    , only{' '}
                    <ProblemBlank
                      placeholder="numerator / achieved"
                      width={220}
                      value={formData.problemStatementParts?.numerator || ''}
                      onChange={handleProblemPartChange('numerator')}
                    />
                    {' '}was reached, resulting in{' '}
                    <ProblemBlank
                      placeholder="gap / impact"
                      width={220}
                      value={formData.problemStatementParts?.gap || ''}
                      onChange={handleProblemPartChange('gap')}
                    />
                    . This is mainly due to{' '}
                    <ProblemBlank
                      placeholder="root cause"
                      width={240}
                      value={formData.problemStatementParts?.rootCause || ''}
                      onChange={handleProblemPartChange('rootCause')}
                    />
                    , which if not addressed, may lead to{' '}
                    <ProblemBlank
                      placeholder="implication"
                      width={240}
                      value={formData.problemStatementParts?.implication || ''}
                      onChange={handleProblemPartChange('implication')}
                    />
                    .
                  </Typography>
                </Paper>
                {errors.problemStatement && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.problemStatement}
                  </Typography>
                )}
              </Box>

              {/* Indicator Description */}
              <Box sx={{ width: '100%', mt: 3, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                Key performance indicators selection
                </Typography>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Do you want to use one of the MOH 515 indicators?
                  </Typography>
                  <RadioGroup
                    row
                    name="useCustomIndicator"
                    value={formData.useCustomIndicator ? "no" : "yes"}
                    onChange={(e) => {
                      const useCustom = e.target.value === "no";
                      setFormData(prev => ({
                        ...prev,
                        useCustomIndicator: useCustom,
                        selectedKPI: useCustom ? undefined : prev.selectedKPI
                      }));
                    }}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>

                {/* Show MOH 515 indicator selection when "Yes" is selected */}
                {!formData.useCustomIndicator && (
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Select one of the MOH 515 indicators
                    </Typography>

                    <FormControl fullWidth>
                      <InputLabel id="moh515-select-label">Use the MOH 515 indicators</InputLabel>
                      <Select
                        labelId="moh515-select-label"
                        id="moh515-select"
                        value={formData.selectedKPI || ''}
                        label="Use the MOH 515 indicators"
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            selectedKPI: e.target.value as string
                          }));
                        }}
                        required
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 'auto',
                            },
                          },
                        }}
                      >
                        {/* MOH 515 data elements - Complete list with section headers */}
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Household Indicators</ListSubheader>
                        <MenuItem value="total_households">1. Total number of households</MenuItem>
                        <MenuItem value="households_visited_new">2. Total number of households visited by CHVs in the month (New Visit)</MenuItem>
                        <MenuItem value="households_visited_revisit">3. Total number of households visited in the month by CHVs (Revisit)</MenuItem>
                        <MenuItem value="households_health_insurance_new">4. Total number of households visited in the Month with upto date health insurance (New Visit)</MenuItem>
                        <MenuItem value="households_uhc_new">5. Total number of households visited in the Month with upto date health insurance-UHC (New Visit)</MenuItem>
                        <MenuItem value="households_nhif_new">6. Total number of households visited in the Month with upto date health insurance-NHIF (New Visit)</MenuItem>
                        <MenuItem value="households_others_new">7. Total number of households visited in the Month with upto date health insurance-OTHERS (New Visit)</MenuItem>
                        <MenuItem value="households_safe_water_new">8. Total number of NEW households visited in the month accessing safe water (New Visit)</MenuItem>
                        <MenuItem value="households_handwashing_new">9. Number of households with hand washing facilities e.g. leaky tins in use (New Visit)</MenuItem>
                        <MenuItem value="households_latrines_new">10. Number of households with functional latrines (New Visit)</MenuItem>
                        <MenuItem value="households_refuse_disposal_new">11. Number of households with Refuse disposal facility (New Visit)</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>FP, ANC and Delivery</ListSubheader>
                        <MenuItem value="women_fp_counselled">12. Number of women 15-49yrs counselled on FP methods</MenuItem>
                        <MenuItem value="women_fp_commodities">13. Number of women 15-49yrs provided with FP commodities</MenuItem>
                        <MenuItem value="women_fp_referred">14. Number of women 15-49yrs Referred for FP Services</MenuItem>
                        <MenuItem value="pregnant_women">15. Number of women who are pregnant</MenuItem>
                        <MenuItem value="underage_pregnancies">16. Number of new under-age pregnancies (under 18 years)</MenuItem>
                        <MenuItem value="pregnant_anc_counselled">17. Number of pregnant women counselled on ANC services</MenuItem>
                        <MenuItem value="pregnant_referred">18. Number of pregnant women Referred to health facility</MenuItem>
                        <MenuItem value="deliveries_cu">19. Number of New deliveries that took place in C.U</MenuItem>
                        <MenuItem value="deliveries_facility">20. Number of New deliveries that took place in Health Facility</MenuItem>
                        <MenuItem value="deliveries_home">21. Number of New deliveries that took place at Home</MenuItem>
                        <MenuItem value="underage_deliveries">22. Number of New under-age Deliveries (10-19 years) in the month</MenuItem>
                        <MenuItem value="mothers_visited_48hrs">23. Number of new mothers visited within 48 hrs of delivery</MenuItem>
                        <MenuItem value="newborns_visited_48hrs">24. Number of new-borns visited at home within 48 hours of delivery</MenuItem>
                        <MenuItem value="home_delivery_pnc_referred">25. Home delivery referred for Post Natal Care (PNC) Services</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Under 5</ListSubheader>
                        <MenuItem value="child_muac_red">26. Child 6-59 months with MUAC (Red) indicating severe malnutrition</MenuItem>
                        <MenuItem value="child_muac_yellow">27. Child 6-59 months with MUAC (Yellow) indicating moderate malnutrition</MenuItem>
                        <MenuItem value="children_dewormed">28. Number of children 12-59 months dewormed</MenuItem>
                        <MenuItem value="child_vitamin_a_referred">29. Child 6-59 months referred for Vitamin A supplementation</MenuItem>
                        <MenuItem value="children_immunization_referred">30. Children of 0-11 months referred for immunization</MenuItem>
                        <MenuItem value="immunization_defaulter_referred">31. Immunization defaulter referred</MenuItem>
                        <MenuItem value="children_developmental_milestones_referred">32. Children with delayed developmental milestones referred</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>HIV Services</ListSubheader>
                        <MenuItem value="hts_referred">33. Referred for HIV Testing Services (HTS)</MenuItem>
                        <MenuItem value="art_defaulter">34. ART defaulter in the Month</MenuItem>
                        <MenuItem value="hiv_exposed_infant_defaulters">35. HIV exposed infant defaulters In the month</MenuItem>
                        <MenuItem value="art_defaulter_traced">36. ART defaulter traced and referred</MenuItem>
                        <MenuItem value="hiv_exposed_infant_traced">37. HIV exposed infant defaulters traced and referred</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>TB Services</ListSubheader>
                        <MenuItem value="tb_screened">38. Number of persons screened for TB</MenuItem>
                        <MenuItem value="presumptive_tb_referred">39. Number of presumptive TB persons referred for TB diagnosis</MenuItem>
                        <MenuItem value="tb_confirmed_cases">40. Number of bacteriologically confirmed TB cases (From TB clinic)</MenuItem>
                        <MenuItem value="tb_contacts_referred">41. Number of presumptive TB contacts of bacteriologically confirmed TB cases referred for Screening</MenuItem>
                        <MenuItem value="tb_contacts_children_ipt">42. Number of children &lt;5 years TB contacts of bacteriologically confirmed TB cases referred for IPT</MenuItem>
                        <MenuItem value="tb_treatment_interrupters">43. Number of TB Treatment Interrupters in the Month (TB clinic)</MenuItem>
                        <MenuItem value="tb_interrupters_traced">44. Number of TB Treatment Interrupters traced</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Known cases of chronic illness referred</ListSubheader>
                        <MenuItem value="diabetes_referred">45. Known cases of Diabetes referred</MenuItem>
                        <MenuItem value="cancer_referred">46. Known cases of Cancer referred</MenuItem>
                        <MenuItem value="hypertension_referred">47. Known cases of Hypertension referred</MenuItem>
                        <MenuItem value="mental_illness_referred">48. Known cases of Mental Illness referred</MenuItem>
                        <MenuItem value="older_persons_referred">49. Older persons (60 years or more) referred for comprehensive geriatric services (Check ups)</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Referred patients reaching health facility</ListSubheader>
                        <MenuItem value="fp_services_reached">50. Number of women referred for FP Services reaching the health facility</MenuItem>
                        <MenuItem value="anc_services_reached">51. Number of Pregnant Women referred for ANC services reaching the health facility</MenuItem>
                        <MenuItem value="pnc_services_reached">52. Number of women referred for Post natal services reaching the health facility</MenuItem>
                        <MenuItem value="immunization_defaulters_reached">53. Number of Immunization defaulters referred reaching the health facility</MenuItem>
                        <MenuItem value="hts_reached">54. Number of people referred for HIV testing services reaching the health facility</MenuItem>
                        <MenuItem value="art_defaulters_reached">55. Number of ART Defaulters referred reaching the health facility</MenuItem>
                        <MenuItem value="hiv_exposed_infant_reached">56. Number of HIV exposed Infant defaulters reaching the health facility</MenuItem>
                        <MenuItem value="older_persons_reached">57. Number of older persons referred for routine check up reaching the health facility</MenuItem>
                        <MenuItem value="presumptive_tb_reached">58. Number of Presumptive TB cases referred reaching the health facility</MenuItem>
                        <MenuItem value="tb_contacts_reached">59. Number of Presumptive TB contacts referred reaching the health facility</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Management and Treatment &lt;5years</ListSubheader>
                        <MenuItem value="tb_contacts_ipt_reached">60. Number of &lt;5 Child TB contacts for Isoniazid Preventive Therapy referred reaching the health facility</MenuItem>
                        <MenuItem value="tb_interruptors_reached">61. Number of TB Interruptors referred reaching the health facility</MenuItem>
                        <MenuItem value="under5_fever_cases">62. Number of under 5 years cases with fever for less than 7 days</MenuItem>
                        <MenuItem value="under5_fever_rdt">63. Number of under 5 years cases with fever for less than 7 days with RDT done</MenuItem>
                        <MenuItem value="under5_fever_rdt_positive">64. Number of under 5 years cases with fever for less than 7 days with (RDT +ve) results</MenuItem>
                        <MenuItem value="under5_malaria_act">65. Number of under 5 years Malaria Cases (RDT +ve) treated with ACT</MenuItem>
                        <MenuItem value="children_fast_breathing">66. Number of children aged 2-59 months presenting with fast breathing</MenuItem>
                        <MenuItem value="children_fast_breathing_amoxycillin">67. Number of children aged 2-59 months presenting with fast breathing treated with Amoxycillin (DT)</MenuItem>
                        <MenuItem value="children_diarrhoea">68. Number of cases of diarrhoea identified in children 2-59 months age</MenuItem>
                        <MenuItem value="children_diarrhoea_zinc_ors">69. Number of children of 2-59 months with diarrhoea treated with Zinc and ORS</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Management and Treatment &gt;5years</ListSubheader>
                        <MenuItem value="over5_fever_cases">70. Number of over 5 years cases with fever for less than 7 days</MenuItem>
                        <MenuItem value="over5_fever_rdt">71. Number of over 5 years cases with fever for less than 7 days with RDT done</MenuItem>
                        <MenuItem value="over5_fever_rdt_positive">72. Number of over 5 years cases with fever for less than 7 days with (RDT +ve) results</MenuItem>
                        <MenuItem value="over5_malaria_act">73. Number of over 5 years Malaria Cases (RDT +ve) treated with ACT</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Community Activities</ListSubheader>
                        <MenuItem value="community_dialogue_days">74. Total number of community dialogue days held</MenuItem>
                        <MenuItem value="community_action_days">75. Total number of community Action Days held</MenuItem>
                        <MenuItem value="community_units_meetings">76. Total number of community units monthly meetings held</MenuItem>
                        <ListSubheader sx={{ fontWeight: 'bold' }}>Deaths in CU</ListSubheader>
                        <MenuItem value="deaths_0_28_days">77. No. of deaths in the month, 0-28 days</MenuItem>
                        <MenuItem value="deaths_29_days_11_months">78. No. of deaths in the month, 29 days-11 months</MenuItem>
                        <MenuItem value="deaths_12_59_months">79. No. of deaths in the month, 12-59 months</MenuItem>
                        <MenuItem value="deaths_6_59_years">80. No. of deaths in the month, 6-59 years (not maternal)</MenuItem>
                        <MenuItem value="deaths_60_years_above">81. No. of deaths in the month, 60 years and above</MenuItem>
                        <MenuItem value="maternal_deaths">82. No. of Maternal deaths in the month</MenuItem>
                      </Select>

                    </FormControl>
                  </Box>
                )}

                {/* Show custom KPI input when "No" is selected */}
                {formData.useCustomIndicator && (
                  <>
                    <Box sx={{ width: '100%', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                        State the key performance indicator for this project
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        name="customKpiDefinition"
                        value={formData.customKpiDefinition || ''}
                        onChange={handleInputChange}
                        placeholder=""
                        helperText=""
                        error={!!errors.customKpiDefinition}
                        required
                      />
                    </Box>
                  </>
                )}

                {/* Numerator and Denominator - Required for ALL projects */}
                <Box sx={{ width: '100%', mb: 3, p: 2, border: '2px solid #1976d2', borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                  
                  {/* Numerator Section */}
                  <Box sx={{ mb: 3 }}>
        
                    <Box sx={{ width: '100%', mb: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Indicator Numerator"
                        name="indicatorNumerator"
                        helperText="This is the count of what you have now (currently on hand) eg Number of children who received all required immunizations"
                        value={formData.indicatorNumerator}
                        onChange={handleInputChange}
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                        required
                        error={!!errors.indicatorNumerator}
                      />
                    </Box>
                  </Box>
                  
                  {/* Denominator Section */}
                  <Box>
              
                    <Box sx={{ width: '100%', mb: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Indicator Denominator"
                        name="indicatorDenominator"
                        helperText="This is the count of the expected total or whole population eg Total number of children eligible for all immunization "
                        value={formData.indicatorDenominator}
                        onChange={handleInputChange}
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                        required
                        error={!!errors.indicatorDenominator}
                      />
                    </Box>
                  </Box>
                </Box>

              </Box>

              {/* Improvement Targets */}
              <Box sx={{ width: '100%', mt: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Objective Improvements Targets
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 2, gap: 1 }}>
                  <Typography variant="subtitle1">You want to improve from</Typography>
                  <TextField
                    name="improvementFrom"
                    value={(() => {
                      const numerator = Number(formData.indicatorNumerator);
                      const denominator = Number(formData.indicatorDenominator);
                      
                      if (numerator && denominator && denominator > 0) {
                        return ((numerator / denominator) * 100).toFixed(1);
                      }
                      return formData.improvementFrom || '';
                    })()}
                    onChange={handleInputChange}
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, max: 100 },
                      readOnly: (() => {
                        const numerator = Number(formData.indicatorNumerator);
                        const denominator = Number(formData.indicatorDenominator);
                        return !!(numerator && denominator && denominator > 0);
                      })()
                    }}
                    error={!!errors.improvementFrom}
                    sx={{ 
                      width: 150,
                      '& .MuiInputBase-input': {
                        backgroundColor: (() => {
                          const numerator = Number(formData.indicatorNumerator);
                          const denominator = Number(formData.indicatorDenominator);
                          return numerator && denominator && denominator > 0 ? '#f5f5f5' : 'inherit';
                        })()
                      }
                    }}
                    required
                  />
                  <Typography variant="subtitle1">to</Typography>
                  <TextField
                    name="improvementTo"
                    value={formData.improvementTo}
                    onChange={handleInputChange}
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, max: 100 },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    error={!!errors.improvementTo}
                    sx={{ width: 100 }}
                    required
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Autopopulate the first part, limit the 2nd part to 100.
Do not use the % sign.
                </Typography>
              </Box>

              {/* Fish Bone Diagram Analysis */}
              <Box sx={{ width: '100%', mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Root Cause Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use the Fish Bone Diagram method provided below to do root cause analysis for the identified problem at hand. 
                </Typography>
                

                {/* Effect (Problem Statement) */}
                {(
                  <Box sx={{ width: '100%', mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Problem: {formData.problemStatement}
                    </Typography>
                  </Box>
                )}

                {/* All Cause Sections - wrapped in toggle conditional */}

                {/* People Causes Section */}
                <FishBoneCauseSection
                  title="List all the People Causes"
                  description="Indicate human resource, skill, or personnel factors. Document 1-3 causes."
                  categoryKey="peopleCauses"
                  formData={formData}
                  setFormData={setFormData}
                  addButtonText="Add People Cause"
                />

                {/* Process Causes Section */}
                <FishBoneCauseSection
                  title="List all the Procedure Causes"
                  description="Note gaps or inefficiencies in the process, that is the way work is done. Document 1 - 3 causes "
                  categoryKey="processCauses"
                  formData={formData}
                  setFormData={setFormData}
                  addButtonText="Add Procedure Cause"
                />

                {/* Policy Causes Section */}
                <FishBoneCauseSection
                  title="List all the Policy Causes"
                  description="Note gaps related to existing health service standards/guidelines. Document 1 - 3 causes "
                  categoryKey="policyCauses"
                  formData={formData}
                  setFormData={setFormData}
                  addButtonText="Add Policy Cause"
                />

                 {/* Place Causes Section */}
                 <FishBoneCauseSection
                                  title="List all the Place Causes"
                                  description="Note gaps related to physical factors like rain, distance between places and conditions of infrastructure. Document 1 - 3 causes"
                                  categoryKey="placeCauses"
                                  formData={formData}
                                  setFormData={setFormData}
                                  addButtonText="Add Place Cause"
                                />
                {/* Provision Causes Section */}
                <FishBoneCauseSection
                  title="List all the Provision Causes"
                  description="Note gaps related to availability of materials and equipment necessary for high quality services. Document 1 - 3 causes "
                  categoryKey="provisionCauses"
                  formData={formData}
                  setFormData={setFormData}
                  addButtonText="Add Provision Cause"
                />
               <Button
                  variant="contained"
                  color="primary"
                  sx={{ mb: 3 }}
                  onClick={() => {
                    // Initialize Fish Bone Analysis structure if not already
                    if (!formData.fishBoneAnalysis) {
                      const emptyWhyAnalysis = {
                        why1: '',
                        reason1: '',
                        why2: '',
                        reason2: '',
                        why3: '',
                        reason3: '',
                        why4: '',
                        reason4: '',
                        why5: '',
                        reason5: '',
                        rootCause: ''
                      };

                      const emptyCategoryAnalysis = {
                        causes: '',
                        selectedCause: '',
                        explanation: '',
                        noSelectionExplanation: '',
                        whyAnalysis: { ...emptyWhyAnalysis }
                      };

                      setFormData(prev => ({
                        ...prev,
                        fishBoneAnalysis: {
                          peopleCauses: { ...emptyCategoryAnalysis },
                          processCauses: { ...emptyCategoryAnalysis },
                          policyCauses: { ...emptyCategoryAnalysis },
                          placeCauses: { ...emptyCategoryAnalysis },
                          provisionCauses: { ...emptyCategoryAnalysis }
                        }
                      }));
                    }
                    // Show Fish Bone Diagram visualization
                    // Toggle Fish Bone Diagram visibility
                    setShowFishBoneDiagram(prevState => !prevState);
                  }}
                >
                  {showFishBoneDiagram ? 'Hide Fish Bone Analysis' : 'Fish Bone Analysis'}
                </Button>
                {!showFishBoneDiagram && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 2, fontStyle: 'italic' }}>
                  Tap here to show
                </Typography>}
                {/* Fish Bone Diagram Visualization */}
                {showFishBoneDiagram && formData.fishBoneAnalysis && (
                  <FishBoneDiagram
                    problemStatement={formData.problemStatement}
                    projectTitle={formData.projectTitle}
                    fishBoneAnalysis={formData.fishBoneAnalysis}
                  />
                )}
              </Box>


              {/* Action Plan */}
              <ActionPlan
                formData={formData}
                setFormData={setFormData}
              />


              {/* Project Start Date */}
              <ProjectDateField
                label="Project Start Date"
                id="firstCycleDate"
                name="firstCycleDate"
                value={formData.firstCycleDate}
                onChange={handleInputChange}
                error={errors.firstCycleDate}
                helperText="Date when project will begin"
                required
                minDate={new Date().toISOString().split('T')[0]}
              />

              {/* Project End Date */}
              <ProjectDateField
                label="Project End Date"
                id="projectEndDate"
                name="projectEndDate"
                value={formData.projectEndDate}
                onChange={handleInputChange}
                error={errors.projectEndDate}
                helperText="Must be in future and not more than 6 months from start date"
                required
                minDate={formData.firstCycleDate ? 
                  // Convert dd/mm/yyyy to yyyy-mm-dd for min validation
                  formData.firstCycleDate.includes('/') ? 
                    formData.firstCycleDate.split('/').reverse().join('-') : 
                    formData.firstCycleDate 
                  : new Date().toISOString().split('T')[0]}
                maxDate={formData.firstCycleDate && formData.firstCycleDate.includes('/') ? (() => {
                  const [day, month, year] = formData.firstCycleDate.split('/');
                  const sixMonthsLater = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                  sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
                  return sixMonthsLater.toISOString().split('T')[0];
                })() : undefined}
              />

              {/* Project Update Note */}
              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, mb: 2 }}>
                Project Update will be done monthly
              </Typography>

              {/* Form Actions */}
              <FormActions
                isSubmitting={isSubmitting}
                onSaveAsDraft={handleSaveAsDraft}
                isEditMode={isEditMode}
                isDraftProject={formData.status === 'draft'}
              />
            </Stack>
          </form>
        </Paper>
      </Container>

      {/* Autosave Notification Snackbar */}
      <Snackbar
        open={showAutosaveNotification}
        autoHideDuration={4000}
        onClose={() => setShowAutosaveNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        message={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2">{autosaveMessage}</Typography>
          </Box>
        }
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 500,
            boxShadow: '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14)',
          }
        }}
      />

      {/* Back Button Confirmation Dialog */}
      <Dialog open={showBackDialog} onClose={() => setShowBackDialog(false)}>
        <DialogTitle>Save as Draft?</DialogTitle>
        <DialogContent>
          <Typography>Do you want to save this project as a draft before leaving?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBackDialog(false)}>Cancel</Button>
          <Button onClick={handleBackWithoutSave} color="error">
            No, Don't Save
          </Button>
          <Button onClick={handleBackWithSave} variant="contained" color="primary">
            Yes, Save as Draft
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddProject;